import React, { useState, useEffect, useMemo } from "react";
import { Node, useReactFlow } from "reactflow";
import {
  Paper,
  Button,
  Menu,
  MenuItem,
  Typography,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useTheme } from "@mui/material/styles";
import { getContrastRatio } from "@mui/material";
import { Node_Type } from "api/GenericPathwayInterfaces";
import { handleAddNode } from "components/Flow/Nodes/nodeHandlers";

import ControlPanelButton from "components/Flow/Controls/ControlPanelButton";
import AddIcon from "@mui/icons-material/Add";

interface AddNodeButtonProps {
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<any[]>>;
  systemId: string;
  onNodesReload?: () => Promise<void>;

  getAllNodeTypesFn: () => Promise<Node_Type[]>;
  createNodeFn: (payload: any) => Promise<any>;
  getFlowDataFn: (id: string) => Promise<{ nodes: Node[]; edges: any[] }>;
  parentKey?: string;
}

const AddNodeButton: React.FC<AddNodeButtonProps> = ({
  setNodes,
  setEdges,
  systemId,
  onNodesReload,
  getAllNodeTypesFn,
  createNodeFn,
  getFlowDataFn,
  parentKey,
}) => {
  const [nodeTypes, setNodeTypes] = useState<Node_Type[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuAnchor, setSubmenuAnchor] = useState<null | HTMLElement>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { project } = useReactFlow();
  const theme = useTheme();

  // Load node types
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAllNodeTypesFn();
        setNodeTypes(data);
      } catch (err) {
        console.error("Error fetching node types:", err);
      }
    };
    load();
  }, [getAllNodeTypesFn]);

  // Group node types by category
  const groupedNodeTypes = useMemo(() => {
    const groups: Record<string, Node_Type[]> = {};
    nodeTypes.forEach((nt) => {
      const category = nt.node_category?.name || "Uncategorized";
      if (!groups[category]) groups[category] = [];
      groups[category].push(nt);
    });
    Object.keys(groups).forEach((key) =>
      groups[key].sort((a, b) => a.name.localeCompare(b.name))
    );
    return groups;
  }, [nodeTypes]);

  const sortedCategories = useMemo(
    () => Object.keys(groupedNodeTypes).sort((a, b) => a.localeCompare(b)),
    [groupedNodeTypes]
  );

  // Add node directly
  const handleAddNodeDirect = async (selectedType: number) => {
    await handleAddNode({
      systemId,
      selectedType,
      nodeTypes,
      setNodes,
      setEdges,
      project,
      createNodeFn,
      getFlowDataFn,
      onNodesReload,
      parentKey: parentKey,
    });
    handleCloseMenus();
  };

  // Menu handlers
  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleOpenSubmenu = (
    e: React.MouseEvent<HTMLElement>,
    category: string
  ) => {
    setActiveCategory(category);
    setSubmenuAnchor(e.currentTarget);
  };

  const handleCloseMenus = () => {
    setAnchorEl(null);
    setSubmenuAnchor(null);
    setActiveCategory(null);
  };

  return (
    <>
      <ControlPanelButton
        label="Add"
        icon={<AddIcon fontSize="small" />}
        onClick={handleOpenMenu}
      />

      {/* Category Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenus}
        keepMounted
      >
        {sortedCategories.map((category) => {
          const catColor =
            theme.custom.base.neutral[600].dark ;
          const contrastText =
            getContrastRatio(catColor, "#fff") > 4.5 ? "#fff" : "#000";

          return (
            <MenuItem
              key={category}
              onClick={(e) => handleOpenSubmenu(e, category)}
              sx={{
                backgroundColor:
                  activeCategory === category ? catColor : "transparent",
                color:
                  activeCategory === category ? contrastText : theme.custom.base.text.primary.dark,
              }}
            >
              <ListItemText primary={category} />
              <ListItemIcon sx={{ minWidth: 24 }}>
                <ArrowRightIcon fontSize="small" />
              </ListItemIcon>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Submenu for Node Types */}
      <Menu
        anchorEl={submenuAnchor}
        open={Boolean(submenuAnchor)}
        onClose={() => setSubmenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        keepMounted
      >
        {activeCategory &&
          groupedNodeTypes[activeCategory].map((nt) => (
            <MenuItem key={nt.id} onClick={() => handleAddNodeDirect(nt.id)}>
              <ListItemText primary={nt.name} />
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default AddNodeButton;
